import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import OverviewTab from "./OverviewTab";
import SiteVisitsTab from "./SiteVisitsTab";
import { DateTime } from "components/common/Date";
import { useEnquiryOverview } from "./EnquiryOverviewContext";
import StatusBadge from "components/common/StatusBadge";
import {
  EnquiryStatus,
  enquiryStatusMap,
} from "features/Enquiries/constants/enquiry.constants";
import {
  EnquiryOverviewTabParam,
  enquiryOverviewTabIdFromSearchParam,
  isValidEnquiryOverviewTabParam,
  searchParamForEnquiryOverviewTabId,
} from "./overviewTabSearchParams";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

function isTerminalEnquiryStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "").trim().toUpperCase();
  return s === EnquiryStatus.CONVERTED || s === EnquiryStatus.CANCELLED;
}

const Section = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { enquiry } = useEnquiryOverview();
  const { can } = useAccess();
  const canUpdateEnquiry = can(Permissions.ENQUIRY.UPDATE);

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidEnquiryOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  if (!enquiry) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">This enquiry could not be loaded.</p>
        </Col>
      </Row>
    );
  }

  const displayTitle = enquiry.title?.trim() || enquiry.code || "—";
  const codeLine =
    enquiry.code && enquiry.code !== displayTitle ? enquiry.code : null;

  const activeTab = enquiryOverviewTabIdFromSearchParam(tabQuery);
  const isTerminal = isTerminalEnquiryStatus(enquiry?.status);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForEnquiryOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === EnquiryOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      {codeLine && (
                        <p className="text-muted mb-1">
                          <span className="fw-medium">{codeLine}</span>
                        </p>
                      )}
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={enquiry.status}
                            map={enquiryStatusMap}
                          />
                        </div>
                        {enquiry.created_at && (
                          <>
                            <div className="vr" />
                            <div>
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={enquiry.created_at} />
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto mt-3 mt-md-0">
                    <div className="hstack gap-2 flex-wrap justify-content-md-end">
                      {canUpdateEnquiry && !isTerminal ? (
                        <Link
                          to={`/enquiry-management/enquiries/edit/${enquiry.id}`}
                          className="btn btn-light btn-sm border"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("2");
                      }}
                      href="#"
                    >
                      Site Visits
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <SiteVisitsTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
